<script setup lang="ts">
import { computed, ref } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import type { RawLocation } from "vue-router";

import type { Discount } from "@Modules/billing/Data/Models/DiscountModel";
import type { Invoice } from "@Modules/billing/Data/Models/InvoiceModel";
import BillingCouponView from "@Modules/billing/Views/BillingCouponView.vue";
import BillingDiscountView from "@Modules/billing/Views/BillingDiscountView.vue";
import BillingSubscriptionsView from "@Modules/billing/Views/BillingSubscriptionsView.vue";
import BillingTransactionsView from "@Modules/billing/Views/BillingTransactionsView.vue";
import BillingVatIdView from "@Modules/billing/Views/BillingVatIdView.vue";
import {
  type BillingSubscriptionRow,
  LATEST_INVOICE_COUNT,
  type TransactionFeedbackBanner,
} from "@Modules/billing/ViewModels/BillingViewModel";
import useTranslation from "@Composables/useTranslation";
import SettingsContentBlock from "@Layouts/settings/LayoutBlock.vue";

//
// Setup
const { __ } = useTranslation();
const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5);

//
// Props
interface Props {
  subscriptionRows: BillingSubscriptionRow[];
  subscriptionsReady?: boolean;
  requestedSubscriptionId?: string | null;
  updatedSubscriptionId?: string | null;
  manageSubscription: (subscriptionId: string) => void;
  /** The discount the billable receives, announced above the subscription table */
  discount?: Discount | null;
  invoices: Invoice[];
  /** Support hides a billable's invoices from them, and then the block goes with them */
  areInvoicesHidden?: boolean;
  invoicesReady?: boolean;
  downloadInvoice: (invoice: Invoice, type: string) => void;
  payInvoice: (invoice: Invoice) => void;
  openHostedInvoice: (invoice: Invoice) => void;
  payingInvoiceId?: string | number | null;
  hostedInvoiceId?: string | number | null;
  feedbackBanner?: TransactionFeedbackBanner | null;
  payAll?: (() => void) | null;
  /** How many invoices the billable has in total, which decides the View All link */
  invoiceCount: number;
  viewAllRoute: RawLocation;
  isCouponVisible?: boolean;
  couponErrors?: Record<string, string>;
  redeemCoupon: (code: string) => Promise<unknown>;
  isVatIdVisible?: boolean;
  vatId?: string;
  vatIdErrors?: Record<string, string>;
  submitVatId: (vatId: string) => Promise<unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  subscriptionsReady: false,
  requestedSubscriptionId: null,
  updatedSubscriptionId: null,
  discount: null,
  invoicesReady: false,
  areInvoicesHidden: false,
  payingInvoiceId: null,
  hostedInvoiceId: null,
  feedbackBanner: null,
  payAll: null,
  isCouponVisible: false,
  couponErrors: () => ({}),
  isVatIdVisible: false,
  vatId: "",
  vatIdErrors: () => ({}),
});

//
// Data
const isMobileView = smallerOrEqual("xl");
const maxRows = LATEST_INVOICE_COUNT;
const isRedeemingCoupon = ref<boolean>(false);
const isSavingVatId = ref<boolean>(false);

//
// Computed
/**
 * The section stays on screen while the subscriptions load, so both sections of the page
 * are visible from the first paint. It only disappears once the billable is confirmed to
 * have no subscriptions, which is the original hide rule for free accounts.
 */
const isSubscriptionSectionVisible = computed<boolean>(() => {
  return !props.subscriptionsReady || props.subscriptionRows.length > 0;
});

/**
 * The link is only worth offering once the billable has more invoices than the page
 * itself shows, so an account whose whole history fits on the page has nowhere to go.
 */
const isViewAllVisible = computed<boolean>(() => props.invoiceCount > LATEST_INVOICE_COUNT);

//
// Methods
/**
 * Each form shows its own saving state for as long as the submit it was handed takes,
 * so a slow save keeps only that form's button busy.
 */
async function redeemCouponCode(code: string): Promise<void> {
  isRedeemingCoupon.value = true;

  try {
    await props.redeemCoupon(code);
  } finally {
    isRedeemingCoupon.value = false;
  }
}

async function saveVatId(vatId: string): Promise<void> {
  isSavingVatId.value = true;

  try {
    await props.submitVatId(vatId);
  } finally {
    isSavingVatId.value = false;
  }
}

//
// Exposed Test Vars
defineExpose({
  isSubscriptionSectionVisible,
  isViewAllVisible,
  isRedeemingCoupon,
  isSavingVatId,
});
</script>

<template>
  <div
    class="billing-page"
    :class="{ 'billing-page--mobile': isMobileView }"
  >
    <SettingsContentBlock
      v-if="isSubscriptionSectionVisible"
      class="billing-page__subscription _billing_subscription_block"
    >
      <template #block-title>
        <h6>
          {{ __("settings_billing_page.subscription.title") }}
        </h6>
      </template>
      <template #block-content>
        <div>
          <p>{{ __("settings_billing_page.subscription.description") }}</p>

          <BillingDiscountView :discount="discount" />

          <BillingSubscriptionsView
            :subscription-rows="subscriptionRows"
            :is-ready="subscriptionsReady"
            :requested-subscription-id="requestedSubscriptionId"
            :updated-subscription-id="updatedSubscriptionId"
            :manage-subscription="manageSubscription"
          />
        </div>
      </template>
    </SettingsContentBlock>

    <SettingsContentBlock
      v-if="!areInvoicesHidden"
      class="billing-page__transaction _billing_transaction_block"
    >
      <template #block-title>
        <h6>
          {{ __("settings_billing_page.transaction.title") }}
        </h6>
      </template>
      <template #block-content>
        <div>
          <p>{{ __("settings_billing_page.transaction.description") }}</p>

          <BillingTransactionsView
            :invoices="invoices"
            :is-ready="invoicesReady"
            :max-rows="maxRows"
            :download-invoice="downloadInvoice"
            :pay-invoice="payInvoice"
            :open-hosted-invoice="openHostedInvoice"
            :paying-invoice-id="payingInvoiceId"
            :hosted-invoice-id="hostedInvoiceId"
            :feedback-banner="feedbackBanner"
            :pay-all="payAll"
          >
            <template #feedback-banner-content>
              <slot name="feedback-banner-content" />
            </template>
          </BillingTransactionsView>

          <RouterLink
            v-if="isViewAllVisible"
            class="billing-page__view-all-button _billing_view_all"
            :to="viewAllRoute"
          >
            {{ __("settings_billing_page.subscription.view_all") }}
          </RouterLink>
        </div>
      </template>
    </SettingsContentBlock>

    <SettingsContentBlock
      v-if="isCouponVisible"
      class="billing-page__coupon _billing_coupon"
    >
      <template #block-title>
        <h6>
          {{ __("settings_billing_page.coupon.title") }}
        </h6>
      </template>
      <template #block-content>
        <div>
          <BillingCouponView
            :errors="couponErrors"
            :loading="isRedeemingCoupon"
            :submit="redeemCouponCode"
          />
        </div>
      </template>
    </SettingsContentBlock>

    <SettingsContentBlock
      v-if="isVatIdVisible"
      class="billing-page__vat-id _billing_vat"
    >
      <template #block-title>
        <h6>
          {{ __("settings_billing_page.vat_id.title") }}
        </h6>
      </template>
      <template #block-content>
        <div>
          <BillingVatIdView
            :vat-id="vatId"
            :errors="vatIdErrors"
            :loading="isSavingVatId"
            :submit="saveVatId"
          />
        </div>
      </template>
    </SettingsContentBlock>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.billing-page {
  $self: &;

  &__subscription {
    margin-bottom: spacing.$s;
  }

  &__coupon,
  &__vat-id {
    margin-top: spacing.$s;
  }

  &__view-all-button {
    display: block;
    font-weight: typography.$font-weight-bold;
    margin-top: spacing.$xxs;
    text-align: right;
  }

  &--mobile {
    #{$self}__subscription {
      margin-bottom: spacing.$m;
    }

    #{$self}__coupon,
    #{$self}__vat-id {
      margin-top: spacing.$m;
    }
  }
}
</style>
